import React from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import ClassificationFieldsGroup from '../../../components/classification/ClassificationFieldsGroup';
import useClassificationFields from '../../../components/classification/useClassificationFields';
import {updateCollectionMaterialClassification} from '../collectionTaskApi';

/**
 * "Edit classification" dialog for a Collection Material, mirroring
 * `SourcesEditClassificationDialog` (Sources panel). Pre-fills from the material's own
 * item-level classification (`material.materialClassification` — distinct from the parent
 * Collection Task's legacy `material.classification`), and saves via
 * `updateCollectionMaterialClassification`, which forwards to the linked DataItem on the
 * backend. Only this material's own item is updated — no propagation to other placements.
 */
const CollectionMaterialClassificationDialog = ({open, onClose, material, onSaved}) => {
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState(null);

    const initialLevel = material?.materialClassification?.level || 'U';
    const {level, distribution, compartment, setLevel, setDistribution, setCompartment, fieldsProps} =
        useClassificationFields(initialLevel, {open});

    React.useEffect(() => {
        if (!open) {
            setError(null);
            return;
        }
        const classification = material?.materialClassification || {};
        setLevel(classification.level || 'U');
        setDistribution(Array.isArray(classification.distribution) ? classification.distribution : []);
        setCompartment(Array.isArray(classification.compartment) ? classification.compartment : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, material?.id]);

    const handleSave = React.useCallback(async () => {
        if (!material?.id) return;
        setIsSaving(true);
        setError(null);
        try {
            const updated = await updateCollectionMaterialClassification(material.id, {level, distribution, compartment});
            onSaved?.(updated);
            onClose?.();
        } catch (err) {
            setError(err?.message || 'Failed to update classification.');
        } finally {
            setIsSaving(false);
        }
    }, [material?.id, level, distribution, compartment, onSaved, onClose]);

    const title = material ? (material.title || material.cmNumber || 'Material') : 'Material';

    return (
        <Dialog
            open={open}
            onClose={isSaving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            aria-label="Edit classification"
            // Default `sm` caps at 600px; bump the paper's own max-width to 720px (+20%) so the
            // dialog has more breathing room for the classification fields.
            PaperProps={{sx: {maxWidth: 720}}}
        >
            <DialogTitle noWrap title={title}>
                Edit classification — {title}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <ClassificationFieldsGroup idPrefix="edit-cm-classification" {...fieldsProps}/>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={isSaving}>
                    {isSaving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(CollectionMaterialClassificationDialog);
